import React from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Check, Ban } from 'lucide-react';
import type { OperationStep } from '@/types/operations';
import { CheckTypeBadge, StepStatusBadge } from './presentation';
import { formatRelativeTime } from '@/utils/formatters';
import { Button } from '@/components/ui';
import { pageStyles } from '@/components/utils/PageLayout';
import styles from './OperationsChecklist.module.css';
import shared from '@/components/utils/shared.module.css';

interface StepRowProps {
  step: OperationStep;
  readOnly: boolean;
  busy: boolean;
  onRefresh: (step: OperationStep) => void;
  onConfirm: (step: OperationStep) => void;
  onExempt: (step: OperationStep) => void;
}

const StepRow: React.FC<StepRowProps> = ({ step, readOnly, busy, onRefresh, onConfirm, onExempt }) => {
  const isExempted = step.status === 'EXEMPTED';
  const showActions = !readOnly && !isExempted;

  return (
    <tr>
      <td className={styles['td-title']}>
        <Link
          to={`/operations-checklist/steps/${step.id}`}
          className={styles.link}
        >
          {step.title}
        </Link>
      </td>
      <td><CheckTypeBadge checkType={step.check_type} /></td>
      <td><StepStatusBadge status={step.status} /></td>
      <td className={styles['td-summary']}>{step.summary || '—'}</td>
      <td className={styles['td-last-run']}>
        {step.last_run_ts ? formatRelativeTime(step.last_run_ts) : '—'}
      </td>
      <td>
        <div className={styles['action-group']}>
          {busy && (
            <span className={`${pageStyles.spinner} ${styles['spinner-sm']}`} />
          )}
          {showActions && (
            <>
              {step.check_type === 'AUTO' && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onRefresh(step)}
                  disabled={busy}
                  title="Re-run automatic check"
                  icon={<RefreshCw size={12} />}
                >
                  Re-run
                </Button>
              )}
              {step.check_type === 'MANUAL' && step.status !== 'PASSED' && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={() => onConfirm(step)}
                  disabled={busy}
                  title="Mark as manually confirmed"
                  icon={<Check size={12} />}
                >
                  Confirm
                </Button>
              )}
              {step.status !== 'PASSED' && (
                <Button
                  variant="warning"
                  size="sm"
                  onClick={() => onExempt(step)}
                  disabled={busy}
                  title="Waive this step with a reason"
                  icon={<Ban size={12} />}
                >
                  Exempt
                </Button>
              )}
            </>
          )}
          <Link
            to={`/operations-checklist/steps/${step.id}`}
            className={shared['btn-view']}
          >
            View
          </Link>
        </div>
      </td>
    </tr>
  );
};

export default StepRow;
